import cn from 'classnames'
import React, { ComponentProps, ReactElement } from 'react'
import { amexCardTypes } from 'src/app/RegisterAccount/RegisterAccount'
import { AmexCardTypeFields } from 'src/enums'
import { useTranslate } from 'src/localization/Trns'

import style from './CardDisplayBox.style'

type CreditCardData = {
  cardName: string
  cardholderName: string
  number: string
  expiration: string
  cvc: string
  address: Record<string, string>
  cardType?: AmexCardTypeFields
  issuerImg?: string
}

type CardDisplayBoxProps = {
  className?: string
  cardData: CreditCardData
} & ComponentProps<'div'>

// Generated with Google Gemini
function maskCreditCard(cardNumberString: string): string {
  // The regular expression finds any digit (\d) that is followed by
  // at least four more digits somewhere later in the string.
  // The `g` flag ensures all such digits are replaced, not just the first.
  const regex = /\d(?=.*\d{4})/g
  return cardNumberString.replace(regex, '*')
}

function CardDisplayBox(props: CardDisplayBoxProps): ReactElement {
  const { className = '', cardData, ...rest } = props
  const translate = useTranslate()

  const maskedCardNumber = maskCreditCard(cardData.number)

  const selectedCardType =
    cardData.address.cardType &&
    amexCardTypes.find(
      (amexCardType) => amexCardType.value === cardData.address.cardType,
    )

  return (
    <div className={cn('CardDisplayBox', className)} css={style} {...rest}>
      <div className="container-inner flex-column gap-8">
        <div className="flex-row justify-start gap-12">
          {/* CARD IMAGE DISPLAY*/}
          <div className="flex-row gap-3 align-center">
            {/* 
              We are using issuerImage here, but this should really be Card Art
              We currently cannot get card art until _after_ a credit card has been created.
              If there is no issuerImg for any reason, the whole text will just shift to the left
            */}
            {cardData.issuerImg && (
              <img
                className="issuer-img"
                src={cardData.issuerImg}
                alt="Credit Card"
              />
            )}
            <div className="flex-column">
              <p className="font-h3 bottom-spacer-1">{cardData.cardName}</p>
              <p className="bottom-spacer-1">{cardData.cardholderName}</p>
              <p>{maskedCardNumber}</p>
            </div>
          </div>
          <div className="flex-row gap-5">
            <div className="flex-column">
              <p className="font-form-label bottom-spacer-2">
                {translate('Expiration')}
              </p>
              <p>{cardData.expiration}</p>
              {selectedCardType && (
                <p className="card-type-spacer">{selectedCardType.fieldText}</p>
              )}
            </div>
            <div className="flex-column">
              <p className="font-form-label bottom-spacer-2">
                {translate('CVC')}
              </p>
              <p>{cardData.cvc}</p>
            </div>
          </div>
        </div>
        <div className="flex-row">
          <div className="flex-column gap-3 address-column">
            <p>{cardData.address['address1']}</p>
            <p>{cardData.address['city']}</p>
            <p>{cardData.address['postal']}</p>
          </div>
          <div className="flex-column gap-3 ">
            <p>{cardData.address['address2'] || '\u00A0'}</p>{' '}
            {/* Without &nbsp; here, the formatting/spacing gets goofy */}
            <p>{cardData.address['province']}</p>
            <p>{cardData.address['country']}</p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CardDisplayBox
